import { CheckboxIcon } from '@app/app/_icons/CheckboxIcon';
import { ViolationError } from '@app/lib/form/contracts/ViolationError';
import { useShowErrorState } from '@app/lib/form/hooks/useShowErrorState';
import { isViolationMessage } from '@app/lib/form/isViolationMessage';
import { useTranslations } from 'next-intl';
import { ChangeEvent, ReactNode, useCallback } from 'react';
import { twJoin, twMerge } from 'tailwind-merge';

export interface TestChoiceButtonOption {
    label: ReactNode;
    value: string;
    hiddenInputs?: ReactNode;
    disabled?: boolean;
}

interface Props {
    name: string;
    options: TestChoiceButtonOption[];
    variant?: 'text' | 'default';
    type?: 'radio' | 'checkbox';
    label?: ReactNode;
    error?: ViolationError;
    onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
    value?: string | string[];
    defaultChecked?: string | string[];
    inputContainerClassName?: string;
}

export function TestChoiceButtonInput({
    name,
    options,
    type = 'radio',
    variant = 'default',
    label,
    error,
    onChange,
    value,
    defaultChecked,
    inputContainerClassName,
}: Props) {
    const t = useTranslations();

    const [showError, handleChange] = useShowErrorState(error, onChange);

    const isControlled = value !== undefined;

    const isChecked = useCallback(
        (optionValue: string): boolean => {
            if (isControlled) {
                if (type === 'radio') {
                    return value === optionValue;
                } else {
                    return Array.isArray(value) && value.includes(optionValue);
                }
            } else {
                if (type === 'radio') {
                    return defaultChecked === optionValue;
                } else {
                    return Array.isArray(defaultChecked) && defaultChecked.includes(optionValue);
                }
            }
        },
        [isControlled, type, value, defaultChecked],
    );

    return (
        <div className="flex flex-col gap-2">
            {label != null && <label className="text-sm">{label}</label>}
            <div className="flex flex-row gap-4">
                {options.map(option => (
                    <label
                        key={option.value}
                        htmlFor={`option-${name}-${option.value}`}
                        className={twMerge(
                            'group flex flex-1 cursor-pointer flex-col items-center justify-center',
                            variant === 'default' && 'min-h-0 rounded-lg border border-neutral-150 bg-white p-3 shadow',
                            variant === 'default' &&
                                'has-[.choice-input:checked]:border-primary-500 has-[.choice-input:checked]:shadow-lg',
                            option.disabled && 'cursor-not-allowed opacity-70',
                        )}>
                        <div className="peer flex flex-row-reverse items-center justify-center gap-2">
                            <span className="w-auto text-sm font-normal">{option.label}</span>
                            <div
                                className={twJoin(
                                    'relative flex size-5 items-center justify-center border border-neutral-400',
                                    type === 'radio' && 'rounded-full has-[:checked]:border-primary-500',
                                    type === 'checkbox' &&
                                        'rounded has-[:checked]:border-primary-500 has-[:checked]:bg-primary-500',
                                    inputContainerClassName,
                                )}>
                                <input
                                    id={`option-${name}-${option.value}`}
                                    name={name}
                                    type={type}
                                    className={twMerge(
                                        'peer absolute inset-0 cursor-pointer appearance-none',
                                        'choice-input',
                                        option.disabled && 'cursor-not-allowed',
                                    )}
                                    value={option.value}
                                    onChange={handleChange}
                                    disabled={option.disabled}
                                    {...(isControlled
                                        ? { checked: isChecked(option.value) }
                                        : { defaultChecked: isChecked(option.value) })}
                                />
                                {type === 'radio' && (
                                    <div className="size-2 rounded-full peer-checked:bg-primary-500"></div>
                                )}
                                {type === 'checkbox' && <CheckboxIcon />}
                            </div>
                        </div>

                        {option.hiddenInputs != null && option.hiddenInputs}
                    </label>
                ))}
            </div>
            {showError && isViolationMessage(error) && <div className="text-sm text-red-500">{t(error.message)}</div>}
        </div>
    );
}
